import type { ReactNode } from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { TurnOutcome } from "@common/domain/conversation-results";
import { OUTCOME_GLYPH, OUTCOME_LABEL, pct, type Severity } from "./resultsLogic";

/** Categorical colors for sources (models/files), validated for both light and dark backgrounds. */
export const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"];
export const seriesColor = (sources: string[], source: string) => SERIES_COLORS[Math.max(0, sources.indexOf(source)) % SERIES_COLORS.length];

export function Swatch({ color }: { color: string }) {
  return <Box component="span" sx={{ width: 9, height: 9, borderRadius: "50%", display: "inline-block", flex: "none", bgcolor: color, mr: 0.75 }} />;
}

export function Panel({ title, note, action, children, flush }: { title: string; note?: ReactNode; action?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", alignItems: "baseline", justifyContent: "space-between", px: 2, pt: 1.75, pb: 0.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 500 }}>{title}</Typography>
        {action}
        {note && <Typography variant="body2" color="text.secondary">{note}</Typography>}
      </Box>
      <Box sx={flush ? { pt: 0.5, overflowX: "auto" } : { px: 2, pt: 1.25, pb: 2, display: "grid", gap: 1.25 }}>{children}</Box>
    </Paper>
  );
}

export function StatTile({ k, v, d, tone }: { k: string; v: ReactNode; d: ReactNode; tone?: string }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, px: 2, py: 1.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{k}</Typography>
      <Typography sx={{ fontSize: "2.4rem", fontWeight: 500, lineHeight: 1.1, color: tone ?? "text.primary" }}>{v}</Typography>
      <Typography variant="caption" color="text.secondary" component="div">{d}</Typography>
    </Paper>
  );
}

export function RateRow({ name, code, n, f, color, selected, onClick }: {
  name: string; code?: string; n: number; f: number; color?: string; selected?: boolean; onClick?: () => void;
}) {
  const theme = useTheme();
  return (
    <Box
      component={onClick ? "button" : "div"}
      onClick={onClick}
      aria-pressed={onClick ? selected : undefined}
      title={`${name}: ${f} of ${n}`}
      sx={{
        display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(80px,34%) 96px", gap: 1.5, alignItems: "center",
        px: 1, py: 0.75, border: 0, borderRadius: 1, width: "100%", textAlign: "left", font: "inherit", color: "text.primary",
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : "transparent",
        cursor: onClick ? "pointer" : "default", "&:hover": onClick ? { bgcolor: "action.hover" } : undefined,
      }}
    >
      <Typography variant="body2" component="span" sx={{ minWidth: 0 }}>
        {code && <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.72rem", color: "primary.main", mr: 0.75 }}>{code}</Box>}
        {name}
      </Typography>
      <Box sx={{ height: 8, bgcolor: "action.selected", borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${n ? (f / n) * 100 : 0}%`, bgcolor: color ?? "error.main", borderRadius: 1 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <Box component="b" sx={{ color: "text.primary", fontWeight: 500 }}>{pct(n ? f / n : null)}</Box> {f}/{n}
      </Typography>
    </Box>
  );
}

export function OutcomePip({ outcome }: { outcome: TurnOutcome }) {
  const theme = useTheme();
  const sx = {
    flagged: { bgcolor: "primary.main", color: "primary.contrastText" },
    passed: { bgcolor: "action.selected", color: "text.primary" },
    abort: { bgcolor: alpha(theme.palette.warning.main, 0.15), color: "warning.main", boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.warning.main, 0.5)}` },
  }[outcome];
  return (
    <Box component="span" title={OUTCOME_LABEL[outcome]} sx={{ width: 16, height: 16, borderRadius: "4px", font: "600 9px/16px monospace", textAlign: "center", display: "inline-block", ...sx }}>
      {OUTCOME_GLYPH[outcome]}
    </Box>
  );
}

export function SeverityChip({ severity }: { severity: Severity }) {
  if (severity === "major") return <Chip size="small" color="error" label="Fail · major" />;
  if (severity === "minor") return <Chip size="small" color="warning" variant="outlined" label="Fail · minor" />;
  return <Chip size="small" color="secondary" variant="outlined" label="Pass" />;
}

/** Reviewer count for a table row; filled with a check when the viewer has reviewed it, so reviewers can track their backlog. */
export function ReviewCount({ count, mine }: { count: number; mine: boolean }) {
  if (!count) return null;
  return mine ? (
    <Chip size="small" color="primary" label={`✓ ${count}`} title={`Reviewed by you · ${count} reviewer${count > 1 ? "s" : ""}`} aria-label={`Reviewed by you, ${count} reviewer${count > 1 ? "s" : ""}`} />
  ) : (
    <Chip size="small" variant="outlined" label={count} title={`Not reviewed by you yet · ${count} reviewer${count > 1 ? "s" : ""}`} aria-label={`Not reviewed by you yet, ${count} reviewer${count > 1 ? "s" : ""}`} />
  );
}
